import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Check, Flag, Footprints, Map, MountainSnow, Route } from "lucide-react";
import { fetchRegion, type RegionResponse } from "../api/regions";
import { fetchExploredCells } from "../api/map";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { JsonLd } from "../components/JsonLd";
import { AtlasPanel } from "../components/AtlasPanel";
import { ProgressRing } from "../components/ProgressRing";
import { ProgressBar } from "../components/ProgressBar";
import { StatBlock } from "../components/StatBlock";
import { CollectibleItem } from "../components/CollectibleItem";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { FeatureDetailOverlay } from "../components/map/FeatureDetailOverlay";
import { RegionBoundaryMap } from "../components/map/RegionBoundaryMap";
import { GoalButton } from "../components/GoalButton";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { ImageCredit } from "../components/ImageCredit";
import type { MapFeatureProperties } from "../api/map";
import { isRegionTrailDone, regionTrailSectionsLabel } from "@atlas/shared";
import { plural } from "../lib/place-names";
import { truncateAtWord } from "../lib/text";

const ITEMS_PAGE_SIZE = 24;

function collectedFirst<T extends { collected: boolean }>(list: T[]): T[] {
  return [...list].sort((a, b) => Number(b.collected) - Number(a.collected));
}

function RegionContent({
  data,
  slug,
  isAuthenticated,
}: {
  data: RegionResponse;
  slug: string;
  isAuthenticated: boolean;
}) {
  const navigate = useNavigate();
  // Explored cells (spec 13) for the boundary preview's fill - the same data
  // the map's "explored areas" toggle draws. Authed only; anonymous and
  // prerendered views get the outline alone.
  const exploredQuery = useQuery({
    queryKey: ["map", "explored", "region", slug],
    queryFn: () => fetchExploredCells({ region: slug }),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const exploredCells = isAuthenticated ? exploredQuery.data?.cells ?? null : null;
  const [drawerFeature, setDrawerFeature] = React.useState<MapFeatureProperties | null>(null);
  const [itemsTab, setItemsTab] = React.useState<"peaks" | "trails" | "landmarks">("peaks");
  const itemsPanelRef = React.useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = React.useState<Record<"peaks" | "trails" | "landmarks", number>>({
    peaks: ITEMS_PAGE_SIZE, trails: ITEMS_PAGE_SIZE, landmarks: ITEMS_PAGE_SIZE,
  });

  // Prerendered static pages baked before this field shipped won't carry it.
  const completionPct = data.completionPct ?? data.coveragePct;
  const rawItems = data.items ?? { peaks: [], trails: [], landmarks: [] };
  // Collected first, then the API's own order (height for peaks). Stable sort keeps it.
  const items = React.useMemo(() => ({
    peaks: collectedFirst(rawItems.peaks),
    // ATL-136: a trail is done here when every section inside this region is
    // walked; older payloads without the section counts keep `collected`.
    trails: collectedFirst(rawItems.trails.map((t) => ({ ...t, collected: isRegionTrailDone(t) }))),
    landmarks: collectedFirst(rawItems.landmarks),
  }), [rawItems.peaks, rawItems.trails, rawItems.landmarks]);
  const loadMore = (tab: "peaks" | "trails" | "landmarks") =>
    setVisibleCount((c) => ({ ...c, [tab]: c[tab] + ITEMS_PAGE_SIZE }));
  const loadMoreButton = (tab: "peaks" | "trails" | "landmarks", total: number) =>
    total > visibleCount[tab] ? (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button variant="secondary" onClick={() => loadMore(tab)}>
          Load more ({total - visibleCount[tab]} remaining)
        </Button>
      </div>
    ) : null;
  const itemsTotal = data.itemsTotal ?? data.stats.peaks.total + data.stats.trails.total + data.stats.landmarks.total;
  const itemsCollected = data.itemsCollected ?? data.stats.peaks.value + data.stats.trails.value + data.stats.landmarks.value;

  const showItemsTab = (tab: "peaks" | "trails" | "landmarks") => {
    setItemsTab(tab);
    itemsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const canonicalPath = `/regions/${slug}`;
  const fallbackDescription = `${data.name}${data.subtitle ? ` (${data.subtitle})` : ""}: ${data.stats.peaks.total} ${plural(data.stats.peaks.total, "peak")}, ${data.stats.trails.total} ${plural(data.stats.trails.total, "trail")} and ${data.stats.landmarks.total} ${plural(data.stats.landmarks.total, "landmark")} tracked on Atlas.`;
  // Editorial copy (seeded, spec 09 SEO) wins over the stats-derived
  // fallback whenever it exists - it's the whole point of this pass.
  const hasDescription = !!data.description && data.description.trim().length > 0;
  const metaDescription = hasDescription
    ? truncateAtWord(data.description as string, 200)
    : fallbackDescription;
  // Anonymous visitors (crawlers included - Googlebot renders JS, and this
  // title wins over the prerendered one) get the fact-based title from
  // scripts/prerender.mjs; the personal coverage title is authed-only.
  usePageMeta({
    title: isAuthenticated
      ? `${data.name} - ${completionPct}% complete | Atlas`
      : `${data.name} - ${data.stats.peaks.total} ${plural(data.stats.peaks.total, "peak")}, ${data.stats.trails.total} ${plural(data.stats.trails.total, "trail")} & ${data.stats.landmarks.total} ${plural(data.stats.landmarks.total, "landmark")} | Atlas`,
    description: metaDescription,
    canonicalPath,
    ogImage: data.heroImage ?? undefined,
  });

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Place",
          name: data.name,
          description: hasDescription ? data.description : data.subtitle,
          url: `https://goatlas.co.uk${canonicalPath}`,
        }}
      />
    <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
      {/* hero banner */}
      <div className="atlas-topo" style={{
        position: "relative", borderRadius: "var(--radius-xl)", border: "1px solid var(--border-default)",
        padding: 28, overflow: "hidden", boxShadow: "var(--shadow-md)", flexShrink: 0,
      }}>
        {data.heroImage && (
          <img src={data.heroImage} alt="" aria-hidden="true" style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
          }} />
        )}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(100deg, var(--bg-app) 18%, color-mix(in srgb, var(--bg-app) 60%, transparent) 44%, color-mix(in srgb, var(--bg-app) 12%, transparent) 100%), linear-gradient(0deg, color-mix(in srgb, var(--bg-app) 45%, transparent), transparent 60%)",
        }} />
        <ImageCredit src={data.heroImage} />
        <div className="r-wrap" style={{ position: "relative", display: "flex", alignItems: "center", gap: 28 }}>
          <ProgressRing value={completionPct} size={132} stroke={12} label="Complete" />
          <div className="r-basis-full" style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>{data.subtitle}</div>
            <h1 style={{ font: "var(--type-display)", fontSize: 44, color: "var(--text-primary)", margin: "0 0 12px", letterSpacing: "-.02em" }}>
              {data.name}
            </h1>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Badge variant="gold" dot>
                {itemsCollected} / {itemsTotal} collected
              </Badge>
              <Badge variant="success">{data.stats.trails.value} trails done</Badge>
              <span
                title={
                  data.totalCells
                    ? `${(data.exploredCells ?? 0).toLocaleString()} of ${data.totalCells.toLocaleString()} areas (roughly 700 m squares) you've passed through. Toggle "Show explored areas" on the map to see them.`
                    : "Share of the region's area you've passed through, in roughly 700 m squares."
                }
                style={{ display: "inline-flex" }}
              >
                <Badge variant="neutral">{data.coveragePct}% of area explored</Badge>
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Button
              variant="primary"
              leftIcon={<Map size={16} />}
              onClick={() => void navigate({ to: "/map", search: { region: slug } })}
            >
              View on map
            </Button>
            {isAuthenticated ? (
              <GoalButton targetType="region" targetId={data.id} />
            ) : (
              <Button variant="secondary" onClick={() => void navigate({ to: "/signup" })}>
                Sign up to track progress
              </Button>
            )}
          </div>
        </div>
      </div>

      {hasDescription && (
        <p style={{ color: "var(--text-secondary)", fontSize: "var(--text-md)", lineHeight: 1.65, maxWidth: 720 }}>
          {data.description}
        </p>
      )}

      {/* stat strip — the three item tiles jump to their list below */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
        {(
          [
            { label: "Peaks", value: `${data.stats.peaks.value} / ${data.stats.peaks.total}`, icon: <MountainSnow size={12} />, tab: "peaks" },
            { label: "Trails", value: `${data.stats.trails.value} / ${data.stats.trails.total}`, icon: <Route size={12} />, tab: "trails" },
            { label: "Landmarks", value: `${data.stats.landmarks.value} / ${data.stats.landmarks.total}`, icon: <Flag size={12} />, tab: "landmarks" },
            { label: "Distance here", value: `${data.stats.distanceKm.toLocaleString()} km`, icon: <Footprints size={12} />, tab: null },
          ] as const
        ).map(({ label, value, icon, tab }) =>
          tab ? (
            <button
              key={label}
              type="button"
              onClick={() => showItemsTab(tab)}
              aria-label={`Show ${label.toLowerCase()} in this region`}
              style={{
                background: "var(--surface-card)", border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-lg)", padding: 18, boxShadow: "var(--ring-top)",
                cursor: "pointer", textAlign: "left", font: "inherit",
                transition: "border-color var(--t-colors), box-shadow var(--t-colors)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--border-default)"; e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-subtle)"; e.currentTarget.style.boxShadow = "var(--ring-top)"; }}
            >
              <StatBlock label={label} value={value} icon={icon} />
            </button>
          ) : (
            <div key={label} style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-lg)", padding: 18, boxShadow: "var(--ring-top)" }}>
              <StatBlock label={label} value={value} icon={icon} />
            </div>
          ),
        )}
      </div>

      {data.geojson && <RegionBoundaryMap key={slug} geojson={data.geojson} exploredCells={exploredCells} />}

      {/* everything in this region (ATL-47: stat tiles jump here) */}
      <div ref={itemsPanelRef} style={{ scrollMarginTop: 20 }}>
        <AtlasPanel
          title="In this region"
          action={
            itemsTotal > 0 ? (
              <Badge variant="gold">
                {itemsCollected} / {itemsTotal} · {Math.max(0, itemsTotal - itemsCollected)} to go
              </Badge>
            ) : undefined
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 6, padding: 4, background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-pill)", alignSelf: "flex-start" }}>
              {(
                [
                  ["peaks", "Peaks", items.peaks.length],
                  ["trails", "Trails", items.trails.length],
                  ["landmarks", "Landmarks", items.landmarks.length],
                ] as const
              ).map(([id, label, count]) => {
                const active = itemsTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setItemsTab(id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 7, padding: "0 16px", height: 34,
                      borderRadius: "var(--radius-pill)", cursor: "pointer", border: "none",
                      fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600,
                      background: active ? "var(--accent)" : "transparent",
                      color: active ? "var(--text-on-gold)" : "var(--text-secondary)",
                      boxShadow: active ? "var(--glow-gold-sm)" : "none",
                      transition: "var(--t-colors)",
                    }}
                  >
                    {label}
                    <span style={{
                      fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 700, padding: "1px 6px",
                      borderRadius: "var(--radius-pill)",
                      background: active ? "rgba(0,0,0,.16)" : "var(--surface-card)",
                      color: active ? "var(--text-on-gold)" : "var(--text-muted)",
                    }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {itemsTab === "peaks" && (
              items.peaks.length === 0 ? (
                <EmptyState icon={<MountainSnow size={18} />} title="No peaks tracked in this region" />
              ) : (
                <>
                <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12 }}>
                  {items.peaks.slice(0, visibleCount.peaks).map((p) => (
                    <CollectibleItem
                      key={p.id}
                      name={p.name}
                      collected={p.collected}
                      meta={p.elevationM != null ? <b>{p.elevationM}m</b> : undefined}
                      icon={<MountainSnow size={28} />}
                      checkIcon={<Check size={13} />}
                      onClick={() =>
                        setDrawerFeature({
                          id: p.id,
                          name: p.name,
                          slug: p.slug,
                          type: "peak",
                          status: p.collected ? "collected" : "not_collected",
                          elevation: p.elevationM ?? undefined,
                        })
                      }
                    />
                  ))}
                </div>
                {loadMoreButton("peaks", items.peaks.length)}
              </>
            )
            )}

            {itemsTab === "trails" && (
              items.trails.length === 0 ? (
                <EmptyState icon={<Route size={18} />} title="No trails tracked in this region" />
              ) : (
                <>
                <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12 }}>
                  {items.trails.slice(0, visibleCount.trails).map((t) => {
                    const sectionsLabel = regionTrailSectionsLabel(t);
                    return (
                    <CollectibleItem
                      key={t.id}
                      name={t.name}
                      collected={t.collected}
                      meta={
                        t.distanceKm != null || sectionsLabel ? (
                          // Stacked so the long sections label wraps under the
                          // distance instead of overflowing a narrow tile.
                          <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 0, textAlign: "center" }}>
                            {t.distanceKm != null && <b>{Math.round(t.distanceKm)} km</b>}
                            {sectionsLabel && <span style={{ whiteSpace: "normal" }}>{sectionsLabel}</span>}
                          </span>
                        ) : undefined
                      }
                      icon={<Route size={28} />}
                      checkIcon={<Check size={13} />}
                      onClick={() => void navigate({ to: "/trails/$id", params: { id: t.id } })}
                    />
                    );
                  })}
                </div>
                {loadMoreButton("trails", items.trails.length)}
              </>
            )
            )}

            {itemsTab === "landmarks" && (
              items.landmarks.length === 0 ? (
                <EmptyState icon={<Flag size={18} />} title="No landmarks tracked in this region" />
              ) : (
                <>
                <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12 }}>
                  {items.landmarks.slice(0, visibleCount.landmarks).map((l) => (
                    <CollectibleItem
                      key={l.id}
                      name={l.name}
                      collected={l.collected}
                      meta={l.category ? l.category.replace(/_/g, " ") : undefined}
                      icon={<Flag size={28} />}
                      checkIcon={<Check size={13} />}
                      onClick={() =>
                        setDrawerFeature({
                          id: l.id,
                          name: l.name,
                          slug: l.slug,
                          type: "landmark",
                          status: l.collected ? "collected" : "not_collected",
                        })
                      }
                    />
                  ))}
                </div>
                {loadMoreButton("landmarks", items.landmarks.length)}
              </>
            )
            )}
          </div>
        </AtlasPanel>
      </div>

      {/* progress + missing nearby */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
        <AtlasPanel title="Progress by collection">
          {data.collections.length === 0 ? (
            <EmptyState icon={<Map size={18} />} title="No collection data for this region" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {data.collections.map((c) =>
                c.slug ? (
                  <button
                    key={c.name}
                    type="button"
                    aria-label={`Open ${c.name} collection`}
                    onClick={() => void navigate({ to: "/collections/$slug", params: { slug: c.slug as string } })}
                    style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer", display: "block", width: "100%" }}
                  >
                    <ProgressBar label={c.name} value={c.value} max={c.max} color={c.color ?? "gold"} />
                  </button>
                ) : (
                  <ProgressBar key={c.name} label={c.name} value={c.value} max={c.max} color={c.color ?? "gold"} />
                ),
              )}
            </div>
          )}
        </AtlasPanel>

        <AtlasPanel
          title="Missing nearby"
          action={data.gapCount > 0 ? <Badge variant="gold">+ {data.gapCount} gaps</Badge> : undefined}
        >
          {data.missingNearby.length === 0 ? (
            <EmptyState icon={<MountainSnow size={18} />} title="Nothing nearby - you've explored it all" />
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {data.missingNearby.slice(0, 4).map((p) => (
                  <CollectibleItem
                    key={p.id}
                    name={p.name}
                    meta={p.elevationM != null ? <b>{p.elevationM}m</b> : undefined}
                    image={p.image ?? null}
                    collected={p.collected}
                    icon={<MountainSnow size={28} />}
                    onClick={() =>
                      setDrawerFeature({
                        id: p.id,
                        name: p.name,
                        slug: p.slug,
                        type: "peak",
                        status: p.collected ? "collected" : "not_collected",
                        elevation: p.elevationM ?? undefined,
                      })
                    }
                  />
                ))}
              </div>
              <div style={{ marginTop: 14 }}>
                <Button
                  variant="secondary"
                  block
                  onClick={() => void navigate({ to: "/map", search: { region: slug, filter: "gaps" } })}
                >
                  Show all {data.gapCount} gaps
                </Button>
              </div>
            </>
          )}
        </AtlasPanel>
      </div>

      {drawerFeature && (
        <FeatureDetailOverlay
          feature={drawerFeature}
          onClose={() => setDrawerFeature(null)}
          onShowOnMap={() => {
            const id = drawerFeature.id;
            setDrawerFeature(null);
            void navigate({ to: "/map", search: { region: slug, focus: id } });
          }}
        />
      )}
    </div>
    </>
  );
}

interface RegionPageProps {
  staticSlug?: string;
  staticData?: RegionResponse;
}

export default function RegionPage({ staticSlug, staticData }: RegionPageProps = {}) {
  const params = useParams({ strict: false });
  const slug = staticSlug ?? (params as { slug?: string }).slug ?? "";
  const { user: me } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["regions", slug],
    queryFn: () => fetchRegion(slug),
    enabled: !staticData && !!slug,
    initialData: staticData,
  });

  if (isLoading || (!data && !staticData)) {
    return (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="200px" />
        <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skel key={i} height="80px" />)}
        </div>
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
          <Skel height="220px" />
          <Skel height="220px" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="r-page" style={{ padding: 28, flex: 1, overflow: "auto" }}>
        <EmptyState icon={<Map size={18} />} title="Region not found" />
      </div>
    );
  }

  return <RegionContent data={data} slug={slug} isAuthenticated={!!me} />;
}
